//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { FormField } from '@campaigns/components/shared/product-details-modal/form/FormField';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  isProductImageContentTypeValid,
  isProductImageUrlValid,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import {
  SecondaryButton,
  TertiaryButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CaptionErrorText } from '@components/atoms/text/Text';

export interface UrlTabProps {
  index: number;
  rules: RegisterOptions | undefined;
  onClosePopover: () => void;
  onPreview: () => void;
}

export const UrlTab = ({
  index,
  onClosePopover,
  onPreview,
  rules,
}: UrlTabProps) => {
  const { setValue, watch } = useFormContext<EditableBVSamplingProduct>();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const currentValue = watch(`variations.${index}.imgUrl`);

  const handleCancel = () => {
    setValue(`variations.${index}.imgUrl`, '');
    setErrorMsg('');
    onClosePopover();
  };

  const handlePreview = async () => {
    if (!currentValue) {
      return;
    }
    if (!isProductImageUrlValid(currentValue)) {
      setErrorMsg('Please enter a valid URL');
      return;
    }
    const isValid = await isProductImageContentTypeValid(currentValue);
    if (!isValid) {
      setErrorMsg('Invalid product image URL or unsupported format');
      return;
    }
    onPreview();
  };

  return (
    <Container>
      <FormField
        inputType='textarea'
        label='Image web address'
        name={`variations.${index}.imgUrl`}
        onValueChange={() => setErrorMsg('')}
        placeholder='https://your-awesome-website.com/030524.png'
        rules={rules}
      />
      {errorMsg && (
        <ErrorText>
          <StyledIcon name='error' />
          <CaptionErrorText>{errorMsg}</CaptionErrorText>
        </ErrorText>
      )}
      <ActionButtons>
        <TertiaryButton
          onClick={handleCancel}
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_ADD_VARIANT_IMAGE_URL
          }
        >
          Cancel
        </TertiaryButton>
        <SecondaryButton
          onClick={handlePreview}
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CONFIRM_ADD_VARIANT_IMAGE_URL
          }
        >
          Add image
        </SecondaryButton>
      </ActionButtons>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
